import { LoadingPageContent } from '@ahmdigital/ui';
import { some } from 'lodash/fp';
import { useDispatch, useStore } from 'react-redux';

import { CustomExplainedTerm } from '../detail/utils/format-data-for-content-tile/types';
import constants from '../../../ahm-constants';
import ExplainedTermDetailPage from '../detail';
import fetchAllProductData from '../../../models/product/fetch-all-product-data';
import getMetadata from '../detail/utils/get-metadata';
import getShouldSkipClientSideRequest from '../../../utils/get-should-skip-client-side-request';
import Head from '../../../../utils/head';
import RedirectAdapter from '../../../utils/redirect-adapter';
import useGetExplainedTerm from '../detail/utils/use-get-explained-term';

export type ExplainedPageProps = {
  explainedTerm?: CustomExplainedTerm;
  slug: string;
};

const ExplainedPage = ({ explainedTerm: serverSideExplainedTerm, slug }: ExplainedPageProps) => {
  const shouldSkip = getShouldSkipClientSideRequest();
  const store = useStore();
  const dispatch = useDispatch();

  // Client-side fetch for CMS preview/time-travel support
  const {
    explainedTerm: clientSideExplainedTerm,
    hasErrors,
    isLoading,
  } = useGetExplainedTerm({
    skip: shouldSkip,
    slug,
  });

  // Use server-side data unless in preview/time-travel mode
  const explainedTerm = shouldSkip ? serverSideExplainedTerm : (clientSideExplainedTerm as CustomExplainedTerm);

  // Show loading state only during client-side fetch
  if (!shouldSkip && isLoading) {
    return <LoadingPageContent />;
  }

  // Use RedirectAdapter only for client-side preview/time-travel mode
  if (!shouldSkip && hasErrors) {
    return <RedirectAdapter to={constants.URLS.SALES.ERROR_500} />;
  }

  if (!shouldSkip && !isLoading && !explainedTerm) {
    return <RedirectAdapter to={constants.URLS.SALES.ERROR_404} />;
  }

  // Server-side data missing - redirect to 404
  if (!explainedTerm) {
    return <RedirectAdapter to={constants.URLS.SALES.ERROR_404} />;
  }

  // Only prefetch product data if the explained term has a ProductList reference to avoid unnecessary data fetching on terms that don't reference products
  const hasProductList = some({ __typename: 'ProductList' }, explainedTerm?.refersToContent || []);
  if (hasProductList) {
    fetchAllProductData(store.getState(), dispatch);
  }

  const { metaTitle, metaDescription } = getMetadata({
    metadataObject: explainedTerm.metadataObject,
    slogan: explainedTerm.slogan,
    summary: explainedTerm.summary,
  });

  return (
    <>
      {shouldSkip && (metaTitle || metaDescription) && (
        <Head>
          {metaTitle && <title>{metaTitle}</title>}
          {metaDescription && <meta name="description" content={metaDescription} />}
        </Head>
      )}
      <ExplainedTermDetailPage explainedTerm={explainedTerm} />
    </>
  );
};

export default ExplainedPage;
